import {
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { cx } from "../cx";
import { type Placement, Popover } from "../Popover";
import styles from "./Tooltip.module.css";

export interface TooltipProps {
  content: ReactNode;
  /** A single element that accepts ref-less event props (button, a, span...). */
  children: ReactElement<Record<string, unknown>>;
  placement?: Placement;
  /** ms before showing; avoids flicker when the pointer sweeps across a row of icons. */
  delay?: number;
  disabled?: boolean;
}

export function Tooltip({ content, children, placement = "top-start", delay = 300, disabled }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const id = useId();

  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  const show = () => {
    if (disabled) return;
    clear();
    timer.current = setTimeout(() => setOpen(true), delay);
  };
  const hide = () => {
    clear();
    setOpen(false);
  };
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const child = isValidElement(children)
    ? cloneElement(children, { "aria-describedby": open ? id : undefined })
    : children;

  return (
    <>
      {/* A wrapper span carries the listeners so the child needs no forwarded ref. */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: hover/focus only reveal a description; the child stays the interactive element */}
      <span
        ref={anchorRef}
        className={styles.anchor}
        onPointerEnter={show}
        onPointerLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {child}
      </span>
      <Popover
        open={open}
        onClose={hide}
        anchorRef={anchorRef}
        placement={placement}
        offset={6}
        surface="bare"
        autoFocus={false}
        role="tooltip"
        id={id}
        className={cx(styles.tooltip)}
      >
        {content}
      </Popover>
    </>
  );
}
